import React, { useEffect, useState } from 'react';
import { get, isEmpty } from 'lodash/fp';
import PropTypes from 'prop-types';
import { Dropdown, Container, Divider, Icon } from 'rsuite';
import { useRouteMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  reservationsCreatePath,
  reservationsTodaysPath,
  reservationsUpcomingPath,
  reservationsPendingPath,
  reservationsUpcomingEventsPath,
  reservationsArchivedPath,
  reservationsROItemCreatePath,
  reservationsROPicklistCreatePath,
  reservationsROPicklistListPath,
  reservationsROItemListPath,
  reservationsOrderedItemsReportsPath
} from '../../../routes/Paths';
import { toggleSidebarNav } from '../../../redux/Reservation/actions/components/page-layout';
import { getSidebarNavExpanded } from '../../../redux/Reservation/selectors/components/page-layout';
import { getUserPermissionsAsMap } from '../../../redux/selectors/permission';
import { fetchSettings } from '../../../redux/Settings/sagas/main/routines';
import { getMainSettingsData } from '../../../redux/Settings/selectors/main';
import * as PERMISSIONS from '../../../constants/permissions/reservation';
import {
  hasViewAccess,
  hasModifyAccess,
  viewAccess
} from '../../../helpers/permission';
import MainLayout from '../../../containers/MainLayout';
import MainContent from '../../MainContent';
import SidebarNav from '../../SidebarNav';
import NavLink from '../../NavLink';
import DropdownItemLink from '../../DropdownItemLink';
import { NAV_KEYS, navKeysByPath } from './nav-keys';
import AccessControl from '../../AccessControl';

const ReservationsPageLayout = ({ children }) => {
  const dispatch = useDispatch();
  const { path } = useRouteMatch();
  const [activeKey, setActiveKey] = useState();
  const [openKeys, setOpenKeys] = useState([
    NAV_KEYS.GUEST_CHOICES,
    NAV_KEYS.REPORTS
  ]);
  const sidebarExpanded = useSelector(getSidebarNavExpanded);
  const permissions = useSelector(getUserPermissionsAsMap);
  const mainSettings = useSelector(getMainSettingsData);

  useEffect(() => {
    if (isEmpty(mainSettings)) dispatch(fetchSettings());
  }, []);

  useEffect(
    () => {
      const navKey = get(path, navKeysByPath);
      if (navKey) setActiveKey(navKey);
    },
    [path]
  );

  const handleToggleSidebarNav = () => {
    dispatch(toggleSidebarNav());
  };

  const handleToggleGuestChoices = () => {
    if (openKeys.indexOf(NAV_KEYS.GUEST_CHOICES) > -1)
      setOpenKeys([NAV_KEYS.REPORTS]);
    else setOpenKeys([NAV_KEYS.GUEST_CHOICES, NAV_KEYS.REPORTS]);
  };

  const handleToggleReports = () => {
    if (openKeys.indexOf(NAV_KEYS.REPORTS) > -1)
      setOpenKeys([NAV_KEYS.GUEST_CHOICES]);
    else setOpenKeys([NAV_KEYS.GUEST_CHOICES, NAV_KEYS.REPORTS]);
  };

  return (
    <MainLayout>
      <MainContent>
        <SidebarNav
          activeKey={activeKey}
          expanded={sidebarExpanded}
          onToggle={handleToggleSidebarNav}
          openKeys={openKeys}
        >
          {hasModifyAccess(PERMISSIONS.MODULE, permissions) && (
            <NavLink
              to={reservationsCreatePath}
              eventKey={NAV_KEYS.CREATE}
              icon={<Icon icon="plus" />}
            >
              Create
            </NavLink>
          )}
          {hasViewAccess(PERMISSIONS.MODULE, permissions) && (
            <NavLink
              to={reservationsTodaysPath}
              eventKey={NAV_KEYS.TODAYS}
              icon={<Icon icon="calendar" />}
            >
              Today&apos;s Reservations
            </NavLink>
          )}
          {hasModifyAccess(PERMISSIONS.MODULE, permissions) && (
            <NavLink
              to={reservationsUpcomingPath}
              eventKey={NAV_KEYS.UPCOMING}
              icon={<Icon icon="calendar" />}
            >
              Upcoming Reservations
            </NavLink>
          )}
          {hasModifyAccess(PERMISSIONS.MODULE, permissions) && (
            <NavLink
              to={reservationsPendingPath}
              eventKey={NAV_KEYS.PENDING}
              icon={<Icon icon="calendar" />}
            >
              Pending Reservations
            </NavLink>
          )}
          {hasModifyAccess(PERMISSIONS.MODULE, permissions) && (
            <NavLink
              to={reservationsArchivedPath}
              eventKey={NAV_KEYS.ARCHIVED}
              icon={<Icon icon="calendar-times-o" />}
            >
              Archived Reservations
            </NavLink>
          )}
          {hasModifyAccess(PERMISSIONS.MODULE, permissions) && (
            <NavLink
              to={reservationsUpcomingEventsPath}
              eventKey={NAV_KEYS.UPCOMING_EVENTS}
              icon={<Icon icon="calendar-check-o" />}
            >
              Upcoming Events
            </NavLink>
          )}
          <Divider style={{ margin: 0 }} />
          <AccessControl
            allowWhenAny={[
              viewAccess(PERMISSIONS.RO_ITEM),
              viewAccess(PERMISSIONS.RO_PICKLIST)
            ]}
          >
            <Dropdown
              title="Guest Choices"
              icon={<Icon icon="character-authorize" />}
              eventKey={NAV_KEYS.GUEST_CHOICES}
              placement="rightStart"
              onClick={handleToggleGuestChoices}
            >
              {hasModifyAccess(PERMISSIONS.RO_ITEM, permissions) && (
                <DropdownItemLink
                  to={reservationsROItemCreatePath}
                  eventKey={NAV_KEYS.RO_ITEM_CREATE}
                >
                  <Icon icon="file" /> Create Item
                </DropdownItemLink>
              )}
              {hasModifyAccess(PERMISSIONS.RO_PICKLIST, permissions) && (
                <DropdownItemLink
                  to={reservationsROPicklistCreatePath}
                  eventKey={NAV_KEYS.RO_PICKLIST_CREATE}
                >
                  <Icon icon="file" /> Create Pick List
                </DropdownItemLink>
              )}
              {hasViewAccess(PERMISSIONS.RO_ITEM, permissions) && (
                <DropdownItemLink
                  to={reservationsROItemListPath}
                  eventKey={NAV_KEYS.RO_ITEM_LIST}
                >
                  <Icon icon="list-alt" /> Items
                </DropdownItemLink>
              )}
              {hasViewAccess(PERMISSIONS.RO_PICKLIST, permissions) && (
                <DropdownItemLink
                  to={reservationsROPicklistListPath}
                  eventKey={NAV_KEYS.RO_PICKLIST_LIST}
                >
                  <Icon icon="list-alt" /> Pick Lists
                </DropdownItemLink>
              )}
            </Dropdown>
          </AccessControl>
          <Dropdown
            title="Reports"
            icon={<Icon icon="bar-chart" />}
            eventKey={NAV_KEYS.REPORTS}
            placement="rightStart"
            onClick={handleToggleReports}
          >
            {hasViewAccess(PERMISSIONS.MODULE, permissions) && (
              <DropdownItemLink
                to={reservationsOrderedItemsReportsPath}
                eventKey={NAV_KEYS.ORDERED_ITEMS_REPORT}
              >
                <Icon icon="list" /> Ordered Items
              </DropdownItemLink>
            )}
          </Dropdown>
        </SidebarNav>
        <Container>{children}</Container>
      </MainContent>
    </MainLayout>
  );
};

ReservationsPageLayout.propTypes = {
  children: PropTypes.node.isRequired
};

export default ReservationsPageLayout;
